import { memo, useContext, useMemo } from 'react'
import { generatePath, useHistory } from 'react-router-dom'

import { TextOverflow } from '@guidde-co/shared.text-overflow.text-overflow'

import { Box, Hidden, makeStyles, Tooltip, Typography } from '@material-ui/core'

import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'

import { ReactComponent as InsightsIcon } from 'assets/icons/menu/icon-insights.svg'
import { ReactComponent as SpaceIcon } from 'assets/icons/add-to-space.svg'

import { paths } from 'app/paths'
import { PlaybookType } from 'app/types'

import { useAuth, useBoolean, useQGDuplicate, useRoles } from 'hooks'
import { isQG } from 'modules'

import {
    BackButton,
    PlaybookAnalytics,
    ShareToPlaylist,
    ShareToSpaceDialog,
    SpacedGroup,
    VideoPanelControls,
    VideoStatusContext
} from 'UI/Components'

const useStyles = makeStyles(theme => ({
    title: {
        fontSize: '20px',
        fontWeight: theme.fontWeightBold,
        maxWidth: '600px',
        marginRight: '4px'
    }
}))

const routeSchema = {
    playbook: paths.editPlaybook,
    playlist: paths.editPlaylist,
    quickGuidde: paths.editQuickGuidde
} as const

type Props = {
    playbook: PlaybookType
    revalidatePlaybook: () => void
    isEditMode?: boolean
}

export const PlaybookHeader = memo(({ playbook, revalidatePlaybook, isEditMode }: Props) => {
    const classes = useStyles()

    const history = useHistory()
    const { uid, orgId } = useAuth()

    const { duplicateQG, duplicationIsLoading } = useQGDuplicate()

    const { processFinished } = useContext(VideoStatusContext)
    const { isContentManager, isSuperAdmin, isAdmin, isViewer } = useRoles(playbook.uploadedByOrgId)

    const isOwner = uid === playbook.creator_uid
    const canEdit = processFinished && (isOwner || isContentManager)

    const hideAnalytics =
        (orgId !== playbook.uploadedByOrgId && !isSuperAdmin) ||
        (!isOwner && !isContentManager && !isAdmin)

    const openEditPage = () => {
        history.push({
            pathname: generatePath(routeSchema[playbook.mode], {
                playbookId: playbook.id
            })
        })
    }

    const analyticsDialog = useBoolean()
    const showAddToDialog = useBoolean()
    const addToSpaceDialog = useBoolean()

    const controls = useMemo(() => {
        if (playbook.isPlaylist) {
            return [
                {
                    title: 'View analytics',
                    icon: <InsightsIcon style={{ color: '#000000DE' }} />,
                    hidden: hideAnalytics,
                    onClick: analyticsDialog.setTrue
                },
                {
                    title: 'Add to Space',
                    icon: <SpaceIcon />,
                    onClick: addToSpaceDialog.setTrue
                }
            ]
        }

        return [
            {
                title: 'Save to playlist',
                icon: <PlaylistAddIcon />,
                hidden: isViewer,
                onClick: showAddToDialog.setTrue
            },
            {
                title: 'Add to Space',
                icon: <SpaceIcon />,
                hidden: !canEdit,
                onClick: addToSpaceDialog.setTrue
            },
            {
                title: 'Make a Copy',
                icon: <FileCopyOutlinedIcon />,
                hidden: !isQG(playbook),
                onClick: () => duplicateQG(playbook),
                isLoading: duplicationIsLoading
            },
            {
                title: 'View analytics',
                icon: <InsightsIcon />,
                hidden: hideAnalytics,
                onClick: analyticsDialog.setTrue
            }
        ]
    }, [
        addToSpaceDialog.setTrue,
        analyticsDialog.setTrue,
        canEdit,
        duplicateQG,
        duplicationIsLoading,
        hideAnalytics,
        isViewer,
        playbook,
        showAddToDialog.setTrue
    ])

    return (
        <Hidden smDown>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <SpacedGroup>
                    <BackButton text="" />

                    <TextOverflow lineClamp={1}>
                        <Tooltip title={playbook.title}>
                            <Typography data-cy="video-title" className={classes.title}>
                                {playbook.title}
                            </Typography>
                        </Tooltip>
                    </TextOverflow>
                </SpacedGroup>

                {analyticsDialog.isTrue && (
                    <PlaybookAnalytics
                        playbookId={playbook.id}
                        playbook={playbook}
                        isOwner={isOwner}
                        hideButton={true}
                        isOpen={true}
                        onClose={analyticsDialog.setFalse}
                    />
                )}

                <VideoPanelControls
                    canEdit={canEdit}
                    hideEdit={isEditMode}
                    hideDelete={!isEditMode}
                    onEditClick={openEditPage}
                    playbook={playbook}
                    shareCallback={revalidatePlaybook}
                    controls={controls}
                />
            </Box>
            {showAddToDialog.isTrue && (
                <ShareToPlaylist
                    onClose={showAddToDialog.setFalse}
                    selectedPlaybooks={[playbook]}
                />
            )}
            {addToSpaceDialog.isTrue && (
                <ShareToSpaceDialog
                    isOpen={true}
                    onClose={addToSpaceDialog.setFalse}
                    playbooksIds={[playbook.id]}
                />
            )}
        </Hidden>
    )
})
